import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { useDialogueScene } from '../../hooks/useDialogueScene';
import { useCallerFace, faceDiscStyle, faceImgStyle } from '../../hooks/useCallerFace';
import { audioEngine } from '../../services/audioEngine';
import { replyTick } from '../../services/uiSounds';
import ChoiceChips from '../ChoiceChips';

// Calls — the app that carries quest triggers, tutorials and notifications
// (treatment §3). It has exactly two states: a call log, and a call.
//
// A call is a directed SCENE, identical to the one Book runs in the studio and
// driven by the same engine — the only difference is that it is painted on a
// 260px screen instead of across the bottom of the world. That sameness is the
// point: Lyra can ask for a field, spend a credit, or start a track under
// exactly the same server-side rules as anyone standing in front of you.

export default function PhoneCalls({ onQuestChange }) {
  const scene = useWorldStore((s) => s.scene);
  const phoneCall = useWorldStore((s) => s.phoneCall);
  const closeScene = useWorldStore((s) => s.closeScene);
  const missedCalls = useWorldStore((s) => s.missedCalls);
  const answerCall = useWorldStore((s) => s.answerCall);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const setPhoneApp = useWorldStore((s) => s.setPhoneApp);
  const setPhoneOpen = useWorldStore((s) => s.setPhoneOpen);
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const setListeningRoom = useWorldStore((s) => s.setListeningRoom);
  const clearMissedCall = useWorldStore((s) => s.clearMissedCall);

  /**
   * TAKE THE SEAT — join, then walk in.
   *
   * Khari, 2026-08-04: "the 'call back' button is relabeled and opens the room,
   * transporting me there in the process."
   *
   * Order matters. The seat is CLAIMED first and the room entered second: if
   * the room filled while the invite sat in the log, you find out here, on the
   * phone, instead of being teleported across the city to stand in a doorway.
   */
  const takeSeat = async (call) => {
    try {
      const LR = await import('../../services/listening');
      const d = await LR.join(call.code, myAvatarId);
      setListeningRoom(d.room);
      clearMissedCall(call.sceneKey);
      setPhoneOpen(false);
      setPhoneApp(null);
      if (call.zone) enterInterior(call.zone);
    } catch (e) {
      // Stays in the log so it can be retried — a room that is momentarily
      // unreachable is not the same as an invite that never happened.
      console.warn('[listening] could not take the seat:', e.message);
      alert(e.message || 'That room has closed.');
    }
  };

  const live = phoneCall && scene ? scene : null;
  // The caller's FACE (Khari: "when we are talking to anyone, their face
  // should show") — same avatar the voice resolves to, initial-disc fallback.
  const face = useCallerFace(live?.key || null);
  const conv = useDialogueScene({
    sceneKey: live?.key || null,
    quest: live?.quest || null,
    avatarId: myAvatarId,
  });
  const { messages, input, setInput, send, sending, opening, choices, closed, note, questCall } = conv;

  const endRef = useRef(null);
  const [secs, setSecs] = useState(0);
  const reportedRef = useRef(null);

  // ── THE RADIO YIELDS TO THE CALL (Khari: "duck the radio during phone
  // voice… don't completely mute — just duck") ─────────────────────────────
  // A LIGHT dip for the whole live call, not silence and not per-clip: the
  // city keeps breathing under her voice (the world going dead for a phone
  // call reads as a bug, not attention), and dipping per line would pump the
  // station with every sentence. Held for the call's duration, restored the
  // moment it ends to whatever the music toggle already said.
  useEffect(() => {
    if (!live) return undefined;
    audioEngine.duck(true);
    return () => audioEngine.duck(false);
  }, [live?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── HER VOICE ON THE LINE (Khari, 2026-08-02) ────────────────────────────
  // Every assistant message can carry `audioUrl` — a cached clip in the
  // caller's real voice. Answering the call was the user gesture, so playback
  // is allowed from then on. Each new spoken line replaces the last (people
  // don't talk over themselves), and hanging up cuts the audio dead.
  const audioRef = useRef(null);
  const spokenRef = useRef(-1);           // index of the last message we spoke
  useEffect(() => {
    if (!live) { spokenRef.current = -1; return; }
    const idx = messages.length - 1;
    const m = messages[idx];
    if (!m || m.role !== 'assistant' || idx <= spokenRef.current) return;
    if (m.audioUrl) {
      spokenRef.current = idx;
      try {
        audioRef.current?.pause();
        const a = new Audio(m.audioUrl);
        audioRef.current = a;
        a.play().catch(() => { /* pre-gesture or blocked — the text still carries the call */ });
      } catch { /* the text still carries the call */ }
    } else if (!m.audioKey) {
      // Text-only line on a call — same soft tick as the dock (Khari,
      // 2026-08-03), so a silent reply still announces itself. audioKey
      // pending = the clip is seconds away; those stay quiet and then speak.
      spokenRef.current = idx;
      replyTick();
    }
  }, [messages, live]);
  useEffect(() => () => { audioRef.current?.pause(); }, []);
  useEffect(() => { if (!live) audioRef.current?.pause(); }, [live]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages, sending]);

  // Call duration — a small thing that makes a call feel like a call, and the
  // only honest way to show "this is still connected" without a spinner.
  useEffect(() => {
    if (!live || closed) return undefined;
    setSecs(0);
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [live?.key, closed]);

  // Some objectives the world cannot be asked about — "did you talk to Lyra" has
  // no queryable state anywhere, so the client is the only thing that knows. We
  // report it by TYPE and target, never by numeric objective id: the chain
  // migration re-authors objectives with DELETE + INSERT, so ids are reissued
  // every time the story is edited.
  useEffect(() => {
    if (!closed || !live?.quest || !live?.giverRef) return;
    if (reportedRef.current === live.key) return;
    reportedRef.current = live.key;
    (async () => {
      await questCall(`${live.quest}/progress`, { objectiveType: 'talk_to', targetRef: live.giverRef });
      onQuestChange?.();
    })();
  }, [closed, live?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!live) reportedRef.current = null; }, [live?.key]);

  // ── The call log ──────────────────────────────────────────────────────────
  if (!live) {
    return (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 16px' }}>
        {missedCalls.length === 0 ? (
          <div style={{ font: '600 12.5px system-ui', opacity: 0.45, lineHeight: 1.6, marginTop: 30, textAlign: 'center' }}>
            No calls yet.<br />People find you once you've got something.
          </div>
        ) : (
          missedCalls.map((c) => (
            <MissedRow
              key={c.sceneKey}
              call={c}
              onAnswer={() => (c.kind === 'listening' ? takeSeat(c) : answerCall(c))}
            />
          ))
        )}
      </div>
    );
  }

  // ── In a call ─────────────────────────────────────────────────────────────
  const mmss = `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Face LEFT of the name, not stacked (Khari, 2026-08-03) — the header
          stays one compact row and the transcript keeps its space. */}
      <div style={{
        padding: '8px 16px 9px', textAlign: 'left',
        borderBottom: '1px solid rgba(255,233,196,0.12)', position: 'relative',
        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 11,
      }}>
        <div style={faceDiscStyle(42)}>
          {face?.image
            ? <img src={face.image} alt="" style={faceImgStyle} />
            : (live.from || live.title || '?').charAt(0)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ font: '800 16px system-ui' }}>{live.title}</div>
          <div style={{ font: '600 11px system-ui', opacity: 0.55, marginTop: 1 }}>
            {closed ? 'Call ended' : mmss}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {opening && <div style={{ opacity: 0.5, font: '600 12px system-ui', textAlign: 'center' }}>connecting…</div>}
        {messages.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '88%', padding: '8px 11px', borderRadius: 14, fontSize: 13.5, lineHeight: 1.42,
            background: m.role === 'user' ? 'rgba(255,178,94,0.2)' : 'rgba(255,255,255,0.08)',
            border: m.role === 'user' ? '1px solid rgba(255,178,94,0.35)' : '1px solid rgba(255,255,255,0.1)',
          }}>{m.display || m.content}</div>
        ))}
        {sending && <div style={{ alignSelf: 'flex-start', opacity: 0.55, fontSize: 13 }}>…</div>}
        {!closed && <ChoiceChips choices={choices} onPick={send} disabled={sending} compact />}
        {note && <div style={{ font: '600 11.5px system-ui', color: '#ffd27d', opacity: 0.9 }}>{note}</div>}
        <div ref={endRef} />
      </div>

      {!closed ? (
        <div style={{ padding: '8px 10px 10px', display: 'flex', gap: 7, alignItems: 'center' }}>
          <input
            value={input}
            placeholder="Say something…"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            disabled={sending || opening}
            style={{
              flex: 1, minWidth: 0, padding: '9px 11px', borderRadius: 999,
              border: '1px solid rgba(255,178,94,0.3)', background: 'rgba(255,255,255,0.07)',
              color: '#fff', fontSize: 13.5, fontFamily: 'inherit', outline: 'none',
            }}
          />
          {/* Hanging up is always available — a call is never a door that closed
              (design law 2). The quest stays where it is and she'll ring back.
              Ending a call POCKETS the phone (Khari: "when someone clicks to
              end a call, close the phone") — the conversation is over, the
              street is the next thing you should be looking at. */}
          <button onClick={() => { closeScene(); setPhoneOpen(false); }} style={hangUp} aria-label="Hang up">✕</button>
        </div>
      ) : (
        <div style={{ padding: '10px 12px 12px', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={() => { closeScene(); setPhoneApp(null); setPhoneOpen(false); }}
            style={{
              padding: '9px 20px', borderRadius: 999, border: 'none', cursor: 'pointer',
              font: '700 13px system-ui', background: 'linear-gradient(180deg,#ffb25e,#ff5e78)', color: '#1a0f00',
            }}
          >Done</button>
        </div>
      )}
    </div>
  );
}

// A missed call wears the caller's face too — its own component so the log
// (painted inside PhoneCalls' early return) can use the face hook legally.
function MissedRow({ call, onAnswer }) {
  // A listening invite has no SCENE, so there is nothing to look a face up
  // against — the host's own picture rides on the invite instead. Passing the
  // synthetic `listen:CODE` key here would just 404 the caller endpoint.
  const listening = call.kind === 'listening';
  const face = useCallerFace(listening ? null : call.sceneKey);
  return (
    <button
      onClick={onAnswer}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 11, textAlign: 'left',
        padding: '11px 4px', background: 'none', cursor: 'pointer',
        border: 'none', borderBottom: '1px solid rgba(255,233,196,0.12)', color: '#ffe9c4',
      }}
    >
      <span style={faceDiscStyle(38)}>
        {(listening ? call.image : face?.image)
          ? <img src={listening ? call.image : face.image} alt="" style={faceImgStyle} />
          : call.from.charAt(0)}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: '700 14px system-ui', color: listening ? '#ffd27d' : '#ff8a9c' }}>{call.from}</span>
        <span style={{ display: 'block', font: '600 11px system-ui', opacity: 0.55 }}>
          {listening ? `Saved you a seat · ${call.code}` : `Missed · ${call.role}`}
        </span>
      </span>
      {/* THE BUTTON SAYS WHAT PRESSING IT DOES. "Call back" is wrong for an
          invitation — you are not returning a call, you are taking a seat, and
          the press also walks you into the room. */}
      <span style={{ font: '700 12px system-ui', color: '#7dff9d' }}>
        {listening ? 'Take seat' : 'Call back'}
      </span>
    </button>
  );
}

const hangUp = {
  width: 38, height: 38, flexShrink: 0, borderRadius: '50%', border: 'none',
  cursor: 'pointer', background: '#ff5065', color: '#fff', fontSize: 15,
  display: 'grid', placeItems: 'center',
};
